import { useState, useEffect, useRef, type FormEvent } from 'react';
import {
  LayoutDashboard,
  Globe2,
  BookOpen,
  Sparkles,
  MessagesSquare,
  Users,
  BarChart3,
  Mail,
  Settings2,
  Plus,
  ArrowUpRight,
  ChevronDown,
  Search,
  ShieldCheck,
  LockKeyhole,
  MoreHorizontal,
  LogOut,
  Check,
  Upload,
  Trash2,
  Copy,
  Activity,
  PanelLeft,
  ExternalLink,
  ChevronRight,
  X,
} from 'lucide-react';
import { api, send } from './api';
import {ScanPanel} from './ScanPanel';
import {UnansweredPanel} from './UnansweredPanel';
import {AccountPanel} from './AccountPanel';
import {ConversationsPanel} from './ConversationsPanel';
import {defaultChatSettings} from '../shared/chat-settings';
const sections = [
  ['Dashboard', LayoutDashboard],
  ['Websites', Globe2],
  ['Knowledge Center', BookOpen],
  ['Teach AI', Sparkles],
  ['Unanswered Questions', MessagesSquare],
  ['Conversations', MessagesSquare],
  ['Leads', Users],
  ['Analytics', BarChart3],
  ['WhatsApp / Email', Mail],
  ['Agent Settings', Settings2],
  ['Account', LockKeyhole],
] as const;
type Site = { id: string; name: string; domain: string; settings: any };
type Knowledge = {
  scanWarnings?: string[];
  id?: string;
  site_id: string | null;
  title: string;
  kind: string;
  visibility: string;
  content: string;
  safe_answer: string;
  priority: number;
  expires_at: string | null;
  enabled: boolean;
  translations: Record<string, string>;
};
const emptyKnowledge: Knowledge = {
  site_id: null,
  title: '',
  kind: 'note',
  visibility: 'public',
  content: '',
  safe_answer: '',
  priority: 50,
  expires_at: null,
  enabled: true,
  translations: {},
};
const defaults = {
  welcome: 'Assalam o alaikum! {brand} mein khush aamdeed 😊 Batayein, kis tarah madad karoon?',
  color: '#154f47',
  whatsapp: '',
  notificationEmail: '',
  qualificationQuestions: [
    'What service do you need?',
    'What is your budget?',
    'When would you like to start?',
  ],
  handoffOnUncertain: true,
  requireHumanForPricing: true,
  agentName: 'Team',
  enabled: true,
  webhookUrl: '',
  retentionDays: 90,
};
const age = (date: string) =>
  new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
function Badge({ children, type = '' }: { children: React.ReactNode; type?: string }) {
  return <span className={'badge ' + type}>{children}</span>;
}
function Modal({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: React.ReactNode;
}) {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', listener);
    const before = document.activeElement as HTMLElement;
    const timer = setTimeout(
      () => document.querySelector<HTMLButtonElement>('.modal .icon-button')?.focus(),
      0,
    );
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', listener);
      before?.focus();
    };
  }, []);
  return (
    <div className="modal-backdrop" onClick={close}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            const controls = Array.from(
              e.currentTarget.querySelectorAll<HTMLElement>('button,input,select,textarea,a[href]'),
            ).filter((x) => !(x as HTMLButtonElement).disabled && x.getClientRects().length);
            const first = controls[0],
              last = controls.at(-1);
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last?.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first?.focus();
            }
          }
        }}
      >
        <header>
          <h2>{title}</h2>
          <button className="icon-button" onClick={close} aria-label="Close dialog">
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
export default function App() {
  const [auth, setAuth] = useState<any>();
  const accountId=useRef<string|undefined>(undefined);
  accountId.current=auth?.account?.id;
  const [password, setPassword] = useState('');
  const [email,setEmail]=useState('');
  const [register,setRegister]=useState(false);
  const [accountName,setAccountName]=useState('');
  const [data, setData] = useState<any>();
  const [page, setPage] = useState('Dashboard');
  const [selected, setSelected] = useState('all');
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [smallScreen, setSmallScreen] = useState(window.innerWidth <= 650);
  const [editor, setEditor] = useState<Knowledge | null>(null);
  const [scanSite,setScanSite]=useState<Site|null>(null);
  const [scanAfterSave,setScanAfterSave]=useState(false);
  const [siteEditor, setSiteEditor] = useState<Site | null>(null);
  const [view, setView] = useState<any>();
  const [teach, setTeach] = useState({ title: '', content: '', site_id: '', original: '' });
  const [teachLanguage, setTeachLanguage] = useState('en');
  const [teachVisibility, setTeachVisibility] = useState('public');
  const [teachSafe, setTeachSafe] = useState('');
  const [testCloud,setTestCloud]=useState(false);
  const [testQuestion, setTestQuestion] = useState('');
  const [testReply, setTestReply] = useState('');
  const [rehearsal,setRehearsal]=useState('hy\nap kia provide karty ho\naap islamabad main sirf karty hay\nkia mtlb hay ap ka\naap jo provide karty hay ap ki kia gurnty hay k paisey sahi jaga laga rahon');
  const [rehearsalResults,setRehearsalResults]=useState<any[]>([]);
  const [rehearsalLive,setRehearsalLive]=useState(false);
  async function refresh() {
    const requestedAccount=accountId.current;
    try {
      const result=await api('admin/state');
      if(requestedAccount===accountId.current&&result.account?.id===requestedAccount)setData(result);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => {
    const media = window.matchMedia('(max-width:650px)');
    const change = () => setSmallScreen(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  useEffect(() => {
    api('auth/session')
      .then(setAuth)
      .catch((e) => setError(e.message));
  }, []);
  useEffect(() => {
    if (auth?.authenticated) void refresh();
  }, [auth]);
  async function action(fn: () => Promise<unknown>, message = 'Saved successfully') {
    setBusy(true);
    setError('');
    try {
      await fn();
      setNotice(message);
      await refresh();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function login(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await send(register?'auth/register':'auth/login', register?{email,name:accountName,password}:{password,...(email?{email}:{})});
      setData(undefined);setSelected('all');setPage('Dashboard');setPassword('');
      setAuth(await api('auth/session'));
      setError('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const sites: Site[] = data?.sites || [];
  const initials=(auth?.account?.name||'My workspace').split(/\s+/).slice(0,2).map((part:string)=>part[0]).join('').toUpperCase();
  const scoped = (rows: any[]) =>
    selected === 'all' ? rows : rows.filter((r) => r.site_id === selected);
  const knowledge: Knowledge[] = data?.knowledge || [];
  const conversations = scoped(data?.conversations || []);
  const leads = scoped(data?.leads || []);
  const filteredKnowledge = knowledge.filter(
    (k) =>
      (selected === 'all' || k.site_id === selected || k.site_id === null) &&
      (k.title + ' ' + k.content).toLowerCase().includes(search.toLowerCase()),
  );
  const analytics = data?.analytics || {};
  const siteName = (id: string | null) =>
    id ? sites.find((s) => s.id === id)?.name || id : 'All my websites';
  async function openConversation(id: string) {
    const requestedAccount=accountId.current;
    try {
      const result=await api('admin/conversations/' + id);
      if(requestedAccount===accountId.current)setView(result);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  const snippet = (id: string) =>
    `<script src="${location.origin}/widget.js" data-site-id="${id}" defer></script>`;
  async function saveKnowledge(e: FormEvent) {
    e.preventDefault();
    if (await action(() => send('admin/knowledge', editor), 'Knowledge is ready for the agent'))
      setEditor(null);
  }
  async function saveSite(e: FormEvent) {
    e.preventDefault();
    if (await action(() => send('admin/sites', siteEditor), 'Website settings saved')) {
      if(scanAfterSave)setScanSite(siteEditor);
      setScanAfterSave(false);setSiteEditor(null);
    }
  }
  async function upload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    if (
      await action(
        () => api('admin/upload', { method: 'POST', body: f }),
        'Document extracted. Review its approved public answer before use.',
      )
    )
      form.reset();
  }
  if (!auth)
    return (
      <div className="loading">
        Loading your workspace…{error && <p className="error">{error}</p>}
      </div>
    );
  if (!auth.authenticated)
    return (
      <div className="login-screen">
        <div className="login-art">
          <span className="brand-symbol">A</span>
          <h1>
            Every conversation.
            <br />A new possibility.
          </h1>
          <p>Your websites. Your knowledge. One helpful assistant.</p>
          <div className="orb" />
        </div>
        <form className="login-form" onSubmit={login}>
          <span className="eyebrow">ATLAS WORKSPACE</span>
          <h2>{register?'Create your workspace.':'Welcome back.'}</h2>
          <p>Sign in to manage your website agents.</p>
          {register&&<label>Name<input required value={accountName} onChange={e=>setAccountName(e.target.value)} autoComplete="name"/></label>}
          <label>Email<input type="email" value={email} required={register} onChange={e=>setEmail(e.target.value)} autoComplete="username"/><small>{register?'Your new workspace starts empty.':'Existing owner: you can leave email blank until you set your owner email in Account settings.'}</small></label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={register?16:undefined}
              autoComplete={register?'new-password':'current-password'}
            />
          </label>
          <button className="primary" disabled={busy}>
            {busy ? 'Please wait…' : register?'Create account':'Sign in'}
            <ArrowUpRight size={18} />
          </button>
          {auth.signup&&<button className="text-button" type="button" onClick={()=>{setRegister(!register);setError('');}}>{register?'Already have an account? Sign in':'Create a new account'}</button>}
          {auth.demo && (
            <div className="demo-note">
              <ShieldCheck size={18} />
              <span>
                Local demo password:
                <br />
                <code>change-this-demo-password</code>
              </span>
            </div>
          )}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
        </form>
      </div>
    );
  return (
    <div className="app">
      <aside
        className={'sidebar ' + (menu ? 'mobile-open' : '')}
        inert={(smallScreen && !menu) || !!editor || !!siteEditor || !!view}
      >
        <a className="brand" href="/">
          <span className="brand-symbol">A</span>atlas<span className="brand-dot">.</span>
        </a>
        <div className="workspace-switch">
          <span className="workspace-avatar">{initials}</span>
          <div>
            <strong>{data?.account?.name||auth.account?.name||'My workspace'}</strong>
            <small>{data?.account?.email||auth.account?.email}</small>
          </div>
          <ChevronDown size={15} />
        </div>
        <p className="nav-label">WORKSPACE</p>
        <nav>
          {sections.map(([name, Icon]) => (
            <button
              key={name}
              className={page === name ? 'active' : ''}
              onClick={() => {
                setPage(name);
                setSearch('');
                setMenu(false);
                setNotice('');
              }}
            >
              <Icon size={19} />
              <span>{name}</span>
              {name === 'Conversations' && conversations.length > 0 && (
                <small>{conversations.length}</small>
              )}
              {name === 'Unanswered Questions' && data?.unansweredOpen>0 && <small>{data.unansweredOpen}</small>}
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <div className="system-card">
            <span className="online-dot" />
            <strong>Agents are ready</strong>
            <p>
              {sites.filter((s) => s.settings.enabled).length} active websites ·{' '}
              {data?.provider === 'mock' ? 'Free local mode' : data?.provider === 'gemini' ? 'Gemini configured' : 'AI provider configured'}
            </p>
            <a href={sites[0]?'/widget?site='+encodeURIComponent(sites[0].id):'/'} target="_blank" rel="noreferrer">
              Try your assistant <ArrowUpRight size={15} />
            </a>
          </div>
          <button
            className="profile"
            onClick={async () => {
              await send('auth/logout', {});
              setData(undefined);setEditor(null);setSiteEditor(null);setView(undefined);setSelected('all');setPage('Dashboard');setPassword('');setNotice('');setError('');
              setAuth(await api('auth/session'));
            }}
          >
            <span className="avatar">{initials}</span>
            <div>
              <strong>{auth.account?.name||'Workspace account'}</strong>
              <small>Sign out</small>
            </div>
            <LogOut size={17} />
          </button>
        </div>
      </aside>
      <div className="main" inert={!!editor || !!siteEditor || !!view}>
        <header className="topbar">
          <div>
            <button
              className="mobile-toggle"
              aria-label="Open navigation"
              aria-expanded={menu}
              onClick={() => setMenu(!menu)}
            >
              <PanelLeft size={21} />
            </button>
            <span>Workspace</span>
            <ChevronRight size={14} />
            <strong>{page}</strong>
          </div>
          <div>
            <span className="provider-pill">
              <span className="online-dot" />
              {data?.provider === 'mock' ? 'Free mode' : data?.provider === 'gemini' ? 'Gemini' : 'Configured'}
            </span>
            <a href={sites[0]?'/widget?site='+encodeURIComponent(sites[0].id):'/'} target="_blank" rel="noreferrer">
              Preview my website <ExternalLink size={15} />
            </a>
            <span className="avatar small">SA</span>
          </div>
        </header>
        <main className="content">
          <div className="page-head">
            <div>
              <p className="eyebrow">YOUR AGENT WORKSPACE</p>
              <h1>{page === 'Dashboard' ? 'A little insight. A lot of possibility.' : page}</h1>
              <p>
                {
                  (
                    {
                      Dashboard:
                        'Your agents are welcoming visitors and turning conversations into connections.',
                      Websites: 'One workspace for every website you run.',
                      'Knowledge Center':
                        'Give your agents the information that makes your business unique.',
                      'Teach AI': 'Turn a correction into a better answer for the next visitor.',
                      'Unanswered Questions': 'Review visitor questions, approve answers, and improve the next conversation.',
                      Conversations: 'Follow the conversation, understand the opportunity.',
                      Leads: 'The people behind your next opportunity.',
                      Analytics: 'Understand what visitors need and where your agents can improve.',
                      'WhatsApp / Email': 'Keep your team connected to every qualified enquiry.',
                      'Agent Settings': 'Set the boundaries, tone, and behavior of each assistant.',
                    } as any
                  )[page]
                }
              </p>
            </div>
            <div className="head-actions">
              <select
                aria-label="Filter by website"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                <option value="all">All my websites</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {page==='Account'&&<AccountPanel account={data?.account||auth.account} onUpdated={async()=>{setAuth(await api('auth/session'));await refresh();}}/>}
              {['Dashboard', 'Websites'].includes(page) && (
                <button
                  className="primary"
                  onClick={() =>
                    setSiteEditor({
                      id: '',
                      name: '',
                      domain: location.origin,
                      settings: { ...defaults },
                    })
                  }
                >
                  <Plus size={17} />
                  Add website
                </button>
              )}
              {page === 'Knowledge Center' && (
                <button
                  className="primary"
                  onClick={() =>
                    setEditor({ ...emptyKnowledge, site_id: selected === 'all' ? null : selected })
                  }
                >
                  <Plus size={17} />
                  Add knowledge
                </button>
              )}
            </div>
          </div>
          {error && (
            <div className="error alert" role="alert">
              {error}
              <button onClick={() => setError('')} aria-label="Dismiss error">
                <X size={16} />
              </button>
            </div>
          )}
          {notice && (
            <div className="success alert" role="status">
              <Check size={17} />
              {notice}
              <button onClick={() => setNotice('')} aria-label="Dismiss notice">
                <X size={16} />
              </button>
            </div>
          )}
          {!data ? (
            <div className="panel">Loading workspace data…</div>
          ) : (
            <>
              {page === 'Dashboard' && (
                <>
                  <div className="stats-grid">
                    {[
                      [
                        'Total conversations',
                        selected === 'all' ? analytics.total : conversations.length,
                        MessagesSquare,
                        'Visitor conversations',
                      ],
                      [
                        'Captured leads',
                        selected === 'all' ? analytics.leads : leads.length,
                        Users,
                        'Ready for your team',
                      ],
                      [
                        'Hot opportunities',
                        selected === 'all'
                          ? analytics.hot
                          : conversations.filter((c: any) => c.qualification === 'hot').length,
                        Sparkles,
                        'Budget + intent to act',
                      ],
                      [
                        'Active websites',
                        sites.filter((s) => s.settings.enabled).length,
                        Globe2,
                        'Working together',
                      ],
                    ].map(([label, value, Icon, sub]: any) => (
                      <div className="stat" key={label}>
                        <div>
                          <span>{label}</span>
                          <Icon size={20} />
                        </div>
                        <h2>{value || 0}</h2>
                        <small>{sub}</small>
                      </div>
                    ))}
                  </div>
                  <div className="dashboard-grid">
                    <section className="panel">
                      <div className="panel-head">
                        <div>
                          <h2>Your websites</h2>
                          <p>A helpful agent on every doorstep.</p>
                        </div>
                        <button className="text-button" onClick={() => setPage('Websites')}>
                          View all <ArrowUpRight size={16} />
                        </button>
                      </div>
                      <div className="site-list">
                        {sites.map((s, i) => (
                          <div className="site-row" key={s.id}>
                            <div className={'site-avatar variant-' + i}>
                              <Globe2 size={23} />
                            </div>
                            <div>
                              <strong>{s.name}</strong>
                              <small>{new URL(s.domain).host}</small>
                            </div>
                            <Badge type={s.settings.enabled ? 'green' : 'gray'}>
                              {s.settings.enabled ? 'Active' : 'Paused'}
                            </Badge>
                            <button
                              className="icon-button"
                              aria-label={'Edit ' + s.name}
                              onClick={() => setSiteEditor(s)}
                            >
                              <MoreHorizontal size={19} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        className="add-dashed"
                        onClick={() =>
                          setSiteEditor({
                            id: '',
                            name: '',
                            domain: location.origin,
                            settings: { ...defaults },
                          })
                        }
                      >
                        <Plus size={17} /> Connect another website
                      </button>
                    </section>
                    <section className="knowledge-callout">
                      <span className="round-icon">
                        <Sparkles size={25} />
                      </span>
                      <div>
                        <span className="eyebrow">MAKE IT YOURS</span>
                        <h2>
                          A smarter agent starts
                          <br />
                          with your knowledge.
                        </h2>
                        <p>
                          Teach it the services, answers, and little details your website doesn’t
                          cover.
                        </p>
                      </div>
                      <button onClick={() => setPage('Knowledge Center')}>
                        Open Knowledge Center <ArrowUpRight size={18} />
                      </button>
                      <div className="callout-decoration">✦</div>
                    </section>
                  </div>
                  <div className="dashboard-grid bottom-grid">
                    <section className="panel">
                      <div className="panel-head">
                        <div>
                          <h2>Recent conversations</h2>
                          <p>See what’s on your visitors’ minds.</p>
                        </div>
                        <button className="text-button" onClick={() => setPage('Conversations')}>
                          View all <ArrowUpRight size={16} />
                        </button>
                      </div>
                      {conversations.length ? (
                        <ConversationTable
                          rows={conversations.slice(0, 5)}
                          open={openConversation}
                        />
                      ) : (
                        <div className="empty">
                          <MessagesSquare size={28} />
                          <h3>Your first conversation is a click away.</h3>
                          <p>
                            Open the demo site and ask the assistant about a property or
                            construction project.
                          </p>
                          <a href={sites[0]?'/widget?site='+encodeURIComponent(sites[0].id):'/'} target="_blank" rel="noreferrer">
                            Preview my chat <ArrowUpRight size={15} />
                          </a>
                        </div>
                      )}
                    </section>
                    <section className="panel health">
                      <h2>Agent readiness</h2>
                      <div className="readiness-ring">
                        <span>{knowledge.filter((k) => k.enabled).length}</span>
                        <small>knowledge records</small>
                      </div>
                      <div>
                        <Check size={17} />
                        <span>Website knowledge separated</span>
                      </div>
                      <div>
                        <LockKeyhole size={17} />
                        <span>Restricted content protected</span>
                      </div>
                      <div>
                        <Globe2 size={17} />
                        <span>Multilingual replies enabled</span>
                      </div>
                      <p>
                        {data.provider === 'mock'
                          ? 'Local demo supports eight languages. Connect the AI provider for broader language coverage.'
                          : 'AI provider is configured. Test answers before adding your live websites.'}
                      </p>
                    </section>
                  </div>
                </>
              )}
              {page === 'Websites' && (
                <div className="website-grid">
                  {sites.map((s, i) => (
                    <section className="panel website-card" key={s.id}>
                      <div className="website-card-top">
                        <div className={'site-avatar variant-' + i}>
                          <Globe2 size={26} />
                        </div>
                        <Badge type={s.settings.enabled ? 'green' : 'gray'}>
                          {s.settings.enabled ? 'Active' : 'Paused'}
                        </Badge>
                      </div>
                      <h2>{s.name}</h2>
                      <p>{s.domain}</p>
                      <div className="mini-stats">
                        <span>
                          <strong>{knowledge.filter((k) => k.site_id === s.id).length}</strong>
                          Knowledge records
                        </span>
                        <span>
                          <strong>
                            {
                              (data.conversations || []).filter((c: any) => c.site_id === s.id)
                                .length
                            }
                          </strong>
                          Conversations
                        </span>
                      </div>
                      <label>
                        Embed on your website<code className="embed-code">{snippet(s.id)}</code>
                      </label>
                      <div className="card-actions">
                        <button
                          className="secondary"
                          onClick={() => {
                            navigator.clipboard
                              .writeText(snippet(s.id))
                              .then(() => setNotice('Embed script copied'))
                              .catch(() =>
                                setError('Clipboard unavailable. Select and copy the script.'),
                              );
                          }}
                        >
                          <Copy size={15} />
                          Copy script
                        </button>
                        <button className="text-button" onClick={() => setSiteEditor(s)}>
                          Settings <Settings2 size={16} />
                        </button>
                        <button className="secondary" onClick={()=>setScanSite(s)}>Auto Website Analysis</button>
                      </div>
                    </section>
                  ))}
                </div>
              )}
              {page === 'Knowledge Center' && (
                <>
                  <div className="knowledge-banner">
                    <ShieldCheck size={25} />
                    <div>
                      <strong>The right knowledge. The right boundaries.</strong>
                      <p>
                        Public content can be shared. Private content needs an approved public
                        answer. Internal instructions stay inside your workspace.
                      </p>
                    </div>
                    <Badge type="green">Protected by design</Badge>
                  </div>
                  <section className="panel">
                    <div className="table-toolbar">
                      <div className="search">
                        <Search size={18} />
                        <input
                          aria-label="Search knowledge"
                          placeholder="Search your knowledge…"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>
                      <span>{filteredKnowledge.length} records</span>
                    </div>
                    <div className="table-scroll">
                      <table>
                        <thead>
                          <tr>
                            <th>Knowledge</th>
                            <th>Website</th>
                            <th>Visibility</th>
                            <th>Priority</th>
                            <th>Status</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {filteredKnowledge.map((k) => (
                            <tr key={k.id}>
                              <td>
                                <button className="row-link" onClick={() => setEditor(k)}>
                                  {k.kind === 'instruction' ? (
                                    <LockKeyhole size={18} />
                                  ) : (
                                    <BookOpen size={18} />
                                  )}
                                  <span>
                                    <strong>{k.title}</strong>
                                    <small>
                                      {k.kind}
                                      {k.scanWarnings?.length?' · Template content omitted':''}
                                      {k.expires_at ? ' · expires ' + age(k.expires_at) : ''}
                                    </small>
                                  </span>
                                </button>
                              </td>
                              <td>{siteName(k.site_id)}</td>
                              <td>
                                <Badge
                                  type={
                                    k.visibility === 'public'
                                      ? 'green'
                                      : k.visibility === 'private'
                                        ? 'amber'
                                        : 'gray'
                                  }
                                >
                                  {k.visibility}
                                </Badge>
                              </td>
                              <td>{k.priority}</td>
                              <td>
                                <Badge
                                  type={
                                    !k.enabled ||
                                    (k.expires_at && new Date(k.expires_at) < new Date())
                                      ? 'gray'
                                      : 'green'
                                  }
                                >
                                  {!k.enabled
                                    ? 'Disabled'
                                    : k.expires_at && new Date(k.expires_at) < new Date()
                                      ? 'Expired'
                                      : k.visibility === 'private' && !k.safe_answer
                                        ? 'Needs public answer'
                                        : k.visibility === 'internal'
                                          ? 'Internal only'
                                          : 'Ready'}
                                </Badge>
                              </td>
                              <td>
                                <button
                                  className="icon-button"
                                  aria-label={'Delete ' + k.title}
                                  onClick={() => {
                                    if (confirm('Delete this knowledge record?'))
                                      void action(
                                        () => api('admin/knowledge/' + k.id, { method: 'DELETE' }),
                                        'Knowledge removed',
                                      );
                                  }}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {!filteredKnowledge.length && (
                      <div className="empty">No matching knowledge records.</div>
                    )}
                  </section>
                  <div className="two-col">
                    <section className="panel form-panel">
                      <h2>Upload a document</h2>
                      <p>
                        Extract text from PDF, DOCX, XLSX, TXT, MD, and CSV. Maximum 5 MB. Uploaded
                        knowledge is private by default.
                      </p>
                      <form onSubmit={upload}>
                        <label>
                          Website
                          <select name="site_id">
                            <option value="">Shared across my websites</option>
                            {sites.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Document
                          <input
                            type="file"
                            name="file"
                            required
                            accept=".pdf,.docx,.xlsx,.txt,.md,.csv"
                          />
                        </label>
                        <input type="hidden" name="visibility" value="private" />
                        <button className="secondary" disabled={busy}>
                          <Upload size={16} />
                          Upload & extract
                        </button>
                      </form>
                    </section>
                    <section className="panel priority-panel">
                      <h2>How knowledge wins</h2>
                      <ol>
                        <li>
                          <strong>Restricted boundaries always apply.</strong>
                          <span>
                            Internal text and unapproved private content never reach the reply
                            generator.
                          </span>
                        </li>
                        <li>
                          <strong>Highest priority comes first.</strong>
                          <span>
                            Teach AI corrections default to 100. Expired offers are excluded.
                          </span>
                        </li>
                        <li>
                          <strong>Website knowledge breaks ties.</strong>
                          <span>
                            Site records outrank shared records with equal priority; newer records
                            come next.
                          </span>
                        </li>
                        <li>
                          <strong>No confirmed answer? Ask a human.</strong>
                          <span>
                            The agent offers a safe fallback rather than inventing a claim.
                          </span>
                        </li>
                      </ol>
                    </section>
                  </div>
                </>
              )}
              {page === 'Teach AI' && (
                <div className="two-col">
                  <section className="panel form-panel">
                    <span className="round-icon light">
                      <Sparkles size={25} />
                    </span>
                    <h2>A better answer, from you.</h2>
                    <p>
                      Teach one topic at a time. Write a short, complete answer including any conditions. Add the words visitors use to the topic or question.
                    </p>
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (
                          await action(
                            () =>
                              send('admin/knowledge', {
                                ...emptyKnowledge,
                                site_id: teach.site_id || null,
                                title: teach.title,
                                content: teach.content,
                                kind: 'correction',
                                visibility: teachVisibility,
                                safe_answer: teachVisibility === 'private' ? teachSafe : '',
                                translations: teachVisibility === 'internal' ? {} : { [teachLanguage]: teachVisibility === 'private' ? teachSafe : teach.content },
                                priority: 100,
                              }),
                            'Correction saved. New replies will use it when relevant.',
                          )
                        )
                          { setTeach({ title: '', content: '', site_id: teach.site_id, original: '' }); setTeachSafe(''); setTestReply(''); }
                      }}
                    >
                      <label>
                        Website
                        <select
                          value={teach.site_id}
                          onChange={(e) => setTeach({ ...teach, site_id: e.target.value })}
                        >
                          <option value="">All my websites</option>
                          {sites.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Topic or visitor question
                        <input
                          required
                          value={teach.title}
                          onChange={(e) => setTeach({ ...teach, title: e.target.value })}
                          placeholder="Do you provide architectural design?"
                        />
                      </label>
                      <label>
                        Answer language
                        <select value={teachLanguage} onChange={e => setTeachLanguage(e.target.value)}>
                          {Object.entries({en:'English',roman:'Roman Urdu',ur:'Urdu',ar:'Arabic',fr:'French',zh:'Chinese',es:'Spanish',de:'German'}).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                        </select>
                      </label>
                      <label>
                        Who can use this information?
                        <select value={teachVisibility} onChange={e => {setTeachVisibility(e.target.value); setTeachSafe('');}}>
                          <option value="public">Customer can see this answer</option>
                          <option value="private">Private note + separate customer answer</option>
                          <option value="internal">Team-only note (never used in replies)</option>
                        </select>
                      </label>
                      <label>
                        Original reply (review only)
                        <textarea
                          value={teach.original}
                          onChange={(e) => setTeach({ ...teach, original: e.target.value })}
                          placeholder="Paste the answer you want to improve"
                        />
                      </label>
                      <label>
                        {teachVisibility === 'public' ? 'Short customer answer' : 'Restricted note'}
                        <textarea
                          required
                          value={teach.content}
                          onChange={(e) => setTeach({ ...teach, content: e.target.value })}
                          placeholder="The accurate answer visitors should receive"
                          rows={5}
                        />
                      </label>
                      {teachVisibility === 'private' && <label>Approved customer answer (optional)
                        <textarea value={teachSafe} onChange={e => setTeachSafe(e.target.value)} placeholder="Only this text may reach customers. Leave blank to keep the whole record private." />
                      </label>}
                      <p>Free mode selects relevant sentences. It does not invent wording or automatically translate business facts. Add approved translations in Knowledge Center.</p>
                      <button className="primary" disabled={busy}>
                        <Sparkles size={17} />
                        Teach this answer
                      </button>
                    </form>
                  </section>
                  <section className="panel form-panel">
                    <h2>Try a visitor question</h2>
                    <p>Select a website on the left. Choose local mode or the configured AI provider. This preview creates no lead or notification.</p>
                    <form onSubmit={async e => {e.preventDefault(); await action(async () => {
                      const result = await send('admin/preview', {site_id: teach.site_id, message: testQuestion, language: teachLanguage, useConfiguredProvider:testCloud});
                      setTestReply(result.text + '\nProvider: ' + result.provider);
                    }, 'Preview ready.');}}>
                      <label className="checkbox-label"><input type="checkbox" checked={testCloud} onChange={e=>setTestCloud(e.target.checked)}/>Use configured AI provider (uses API quota)</label>
                      <label>Visitor question<input required maxLength={2000} value={testQuestion} onChange={e => setTestQuestion(e.target.value)} placeholder="aap construction bhi karte hain?" /></label>
                      <button className="secondary" disabled={busy || !teach.site_id}>Test saved knowledge</button>
                    </form>
                    {testReply && <div className="info-note" role="status">{testReply}</div>}
                    <h2>Recently taught</h2>
                    {knowledge
                      .filter((k) => k.kind === 'correction')
                      .map((k) => (
                        <div className="taught" key={k.id}>
                          <Badge type="green">Priority 100</Badge>
                          <h3>{k.title}</h3>
                          <p>{k.content}</p>
                          <small>{siteName(k.site_id)}</small>
                          <button className="text-button" onClick={() => setEditor(k)}>
                            Review correction <ArrowUpRight size={15} />
                          </button>
                        </div>
                      ))}
                    {!knowledge.some((k) => k.kind === 'correction') && (
                      <div className="empty">
                        <Sparkles size={30} />
                        <p>Your approved corrections will appear here.</p>
                      </div>
                    )}
                    <div className="info-note">
                      For a correction in another language, open its knowledge record and add an
                      approved translation. Free mode selects relevant sentences from approved text.
                    </div>
                  </section>
                </div>
              )}
              {page === 'Unanswered Questions' && <UnansweredPanel questions={scoped(data?.unanswered||[])} sites={sites} knowledge={knowledge} busy={busy} onRefresh={refresh} onAnswer={(id,answer,language,shareWorkspace)=>action(()=>send('admin/unanswered/'+id+'/answer',{answer,language,shareWorkspace}),'Answer approved. The agent can use it for future visitors.')} onStatus={(id,status)=>action(()=>send('admin/unanswered/'+id+'/status',{status}),'Question status updated')} onConversation={openConversation} onKnowledge={setEditor}/>}
              {page === 'Conversations' && (
                <ConversationsPanel key={(auth.account?.id||'')+selected} rows={conversations} siteId={selected==='all'?undefined:selected} busy={busy} onRefresh={refresh} onOpen={openConversation} onDelete={ids=>action(()=>send('admin/conversations/bulk-delete',{ids}),`${ids.length} conversations deleted`)}/>
              )}
              {page === 'Leads' && (
                <section className="panel">
                  <div className="panel-head">
                    <div>
                      <h2>Your lead inbox</h2>
                      <p>Contact details are captured only with visitor consent.</p>
                    </div>
                    <Badge type="green">{leads.length} leads</Badge>
                  </div>
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Contact</th>
                          <th>Website</th>
                          <th>Intent</th>
                          <th>Status</th>
                          <th>Conversation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((l: any) => (
                          <tr key={l.id}>
                            <td>
                              <strong>{l.name||'Visitor — contact pending'}</strong>
                              <small className="block">{l.phone}</small><small className="block">{l.email}</small>
                              {l.verification_question&&<small className="block" title={l.verification_question}>Verification: {l.verification_question.slice(0,100)}</small>}
                            </td>
                            <td>{l.site_name}</td>
                            <td>
                              <Badge type={l.qualification === 'hot' ? 'amber' : 'green'}>
                                {l.qualification}
                              </Badge>
                            </td>
                            <td>
                              <select
                                aria-label={'Lead status for ' + l.name}
                                value={l.status}
                                onChange={(e) =>
                                  void action(() =>
                                    send(
                                      'admin/leads/' + l.id,
                                      { status: e.target.value },
                                      'PATCH',
                                    ),
                                  )
                                }
                              >
                                {['new', 'contacted', 'qualified', 'won', 'lost','verification_pending','verification_complete'].map((s) => (
                                  <option key={s} value={s}>{s.replaceAll('_',' ')}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <button
                                className="text-button"
                                onClick={() => openConversation(l.conversation_id)}
                              >
                                Read context <ArrowUpRight size={15} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!leads.length && (
                    <div className="empty">
                      <Users size={30} />
                      <h3>Make room for your next connection.</h3>
                      <p>Visitor contact details and qualification will appear here.</p>
                    </div>
                  )}
                </section>
              )}
              {page === 'Analytics' && (
                <>
                  <div className="stats-grid">
                    {[
                      [
                        'Conversations',
                        selected === 'all' ? analytics.total : conversations.length,
                      ],
                      ['Visitor messages', selected === 'all' ? analytics.messages : '—'],
                      [
                        'Lead conversion',
                        (conversations.length
                          ? Math.round((leads.length / conversations.length) * 100)
                          : 0) + '%',
                      ],
                      [
                        'Human handoffs',
                        conversations.filter((c: any) => c.status === 'human').length,
                      ],
                    ].map(([label, value]) => (
                      <div className="stat" key={label}>
                        <span>{label}</span>
                        <h2>{value}</h2>
                        <small>All time · selected websites</small>
                      </div>
                    ))}
                  </div>
                  <div className="two-col">
                    <section className="panel form-panel">
                      <h2>Conversation languages</h2>
                      <p>The languages your visitors choose.</p>
                      {Array.from(new Set(conversations.map((c: any) => c.language))).map(
                        (lang: any) => {
                          const n = conversations.filter((c: any) => c.language === lang).length;
                          return (
                            <div className="bar-row" key={lang}>
                              <span>
                                {(
                                  {
                                    en: 'English',
                                    ur: 'Urdu',
                                    roman: 'Roman Urdu',
                                    ar: 'Arabic',
                                    fr: 'French',
                                    zh: 'Chinese',
                                    es: 'Spanish',
                                    de: 'German',
                                  } as any
                                )[lang] || lang}
                              </span>
                              <div>
                                <i
                                  style={{
                                    width: (n / Math.max(1, conversations.length)) * 100 + '%',
                                  }}
                                />
                              </div>
                              <strong>{n}</strong>
                            </div>
                          );
                        },
                      )}
                      {!conversations.length && (
                        <div className="empty">Start a conversation to see language analytics.</div>
                      )}
                    </section>
                    <section className="panel form-panel">
                      <h2>Opportunity quality</h2>
                      <p>Qualification based on visitor intent, budget, and timing.</p>
                      {['hot', 'warm', 'general'].map((q) => {
                        const n = conversations.filter((c: any) => c.qualification === q).length;
                        return (
                          <div className="bar-row" key={q}>
                            <span>{q}</span>
                            <div>
                              <i
                                style={{
                                  width: (n / Math.max(1, conversations.length)) * 100 + '%',
                                }}
                              />
                            </div>
                            <strong>{n}</strong>
                          </div>
                        );
                      })}
                      <div className="info-note">
                        {analytics.uncertain} answers needed confirmation across the workspace.
                        Review the conversation history to add missing knowledge.
                      </div>
                    </section>
                  </div>
                </>
              )}
              {['WhatsApp / Email', 'Agent Settings'].includes(page) && (
                <>
                  {page==='Agent Settings' && <section className="panel form-panel">
                    <h2>Gemini connection</h2><small>Version: {data.buildVersion||'Earlier release'}</small>
                    {(data.provider!=='gemini'||!data.geminiStatus?.configured)&&<p className="info-note">Natural conversation is not active on this server. Local demo replies are limited. Enable Gemini and configure its server API key to let the model understand arbitrary visitor messages.</p>}
                    <p>Selected provider: {data.provider}. Model: {data.geminiStatus?.model}.</p>
                    <p>{data.geminiStatus?.configured?'Server API key is configured.':'Add GEMINI_API_KEY in cPanel environment variables.'}</p>
                    <p>Last successful request: {data.geminiStatus?.lastSuccess||'Not tested in this server session'}</p>
                    {data.geminiStatus?.lastError&&<p role="status">{data.geminiStatus.lastError}</p>}
                    {/^Gemini HTTP (500|502|503|504)/.test(data.geminiStatus?.lastError||'')&&<p className="info-note">Google temporarily could not serve the model request. The app retries once within the original timeout, then uses local help. A successful connection test does not guarantee the next chat request will succeed. Repeated 503 errors require the model service to recover.</p>}
                    {data.geminiStatus?.cooldownUntil&&<p>Next model attempt available after: {new Date(data.geminiStatus.cooldownUntil).toLocaleTimeString()}.</p>}
                    {data.geminiStatus?.lastAttempts>0&&<p>Requests used for the last attempt: {data.geminiStatus.lastAttempts} (maximum 2).</p>}
                    <button className="secondary" disabled={busy||!data.geminiStatus?.configured} onClick={()=>void action(async()=>{await send('admin/gemini-test',{});},'Gemini connection verified. One test request used.')}>Test Gemini connection</button>
                    <h3>Conversation quality checks</h3>
                    <p>Select one website and try a complete exchange. Check that the assistant keeps the requested property type, remembers your budget and size, and asks only for missing details.</p>
                    <label>Example conversation<select defaultValue="" onChange={e=>{
                      const examples:Record<string,string>={pricing:'hy\nmujhey property main investments lkarni hy\ncapital main\nhan g residenatial main\nexecutive\n5\naap bata dey kia payemnt plane hay 5 marla k',investment:'mjhy Islamabad mein achi property leni hay\nresidential\nmjhy investment krni hay jo baad mein munafa day\n3 crore hay\naap sirf sawal kar rahay ho\nmain abhi sirf maloomat lay raha hon',property:'mujhey F6 main plot chiey\n3 crore\n5 marla\nmujhey blue area main comercal plot chaiey\n2 crore\n4 marla\nji',flat:'mujhy centurus mall main flat lena hay\n3 crore\n2 bedrooms\nji',social:'hy\nkia hal hay\nteek kia ho raha hy\naap kia provide karty ho\naap robot ho?'};
                      if(examples[e.target.value]){setRehearsal(examples[e.target.value]);setRehearsalResults([]);}
                    }}><option value="" disabled>Choose a test</option><option value="property">Plots and a change of area</option><option value="flat">Flat requirement and handoff</option><option value="social">Greeting, services and AI identity</option><option value="investment">Useful answers without a questionnaire</option><option value="pricing">Project, block, size and payment plan</option></select></label>
                    <label className="checkbox-label"><input type="checkbox" checked={rehearsalLive} onChange={e=>setRehearsalLive(e.target.checked)}/> Use the configured AI provider (may use API quota)</label>
                    <p>{rehearsalLive?'Uses your configured provider with approved knowledge. Review the provider shown for each reply.':'Local mode uses no external API. It checks the local flow, not Gemini conversation quality.'}</p>
                    <label>Visitor messages<textarea rows={9} maxLength={12000} value={rehearsal} onChange={e=>setRehearsal(e.target.value)} /></label>
                    <button className="secondary" disabled={busy||selected==='all'||(rehearsalLive&&(data.provider!=='gemini'||!data.geminiStatus?.configured))} onClick={()=>void action(async()=>{
                      const messages=rehearsal.split('\n').map(s=>s.trim()).filter(Boolean).slice(0,12);
                      if(!messages.length)throw Error('Add at least one message.');
                      const history:{role:string;content:string}[]=[];setRehearsalResults([]);
                      for(const message of messages){
                        const result=await send('admin/preview',{site_id:selected,message:message.slice(0,2000),language:'roman',useConfiguredProvider:rehearsalLive,history:history.slice(-24)});
                        setRehearsalResults(previous=>[...previous,{message,...result}]);
                        history.push({role:'user',content:message.slice(0,2000)},{role:'assistant',content:result.text.slice(0,3000)});
                        if(result.fallbackReason||result.provider==='gemini-fallback')throw Error('Gemini fell back to a local reply. The reason is shown below; this is not a successful conversation test.');
                      }
                    },'Conversation test finished. Review the actual replies below.')}>Run {rehearsalLive?'AI':'local'} conversation test</button>
                    <p>No leads or visitor conversations are created. AI tests stop on a provider error.</p>
                    <div className="transcript">{rehearsalResults.map((r,index)=><div key={index} className="transcript-message"><small>Visitor</small><p>{r.message}</p><small>Assistant · {r.provider}{typeof r.elapsedMs==='number'?' · '+(r.elapsedMs/1000).toFixed(2)+'s':''}{r.fallbackReason?' · '+r.fallbackReason:''}{r.knowledgeSearches?' · Saved-source search completed':''}</small><p>{r.text}</p>{!!r.qualityWarnings?.length&&<p className="info-note">Reply style review: {r.qualityWarnings.join(" · ")}</p>}<details><summary>Knowledge used for this turn ({r.approvedRecords} approved records)</summary><p>Sources cited: {r.sourceTitles?.join(' · ')||'None'}</p><p>Initial passages: {r.contextTitles?.join(' · ')}</p></details></div>)}</div>
                    <p className="info-note">Visitor messages and approved knowledge are processed by Google when Gemini is enabled. Common email and phone patterns are redacted; other sensitive details may remain. Free-tier data may be used by Google to improve its products. Use a free-tier project to avoid paid usage; the app cannot determine your Google billing tier. API errors fall back to local answers.</p>
                  </section>}
                  <div className="settings-grid">
                    {sites
                      .filter((s) => selected === 'all' || s.id === selected)
                      .map((s) => (
                        <section className="panel form-panel" key={s.id}>
                          <div className="panel-head">
                            <div>
                              <h2>{s.name}</h2>
                              <p>{s.domain}</p>
                            </div>
                            <button className="secondary" onClick={() => setSiteEditor(s)}>
                              <Settings2 size={16} />
                              Edit settings
                            </button>
                          </div>
                          {page === 'WhatsApp / Email' ? (
                            <>
                              <div className="setting-line">
                                <span>WhatsApp</span>
                                <strong>
                                  {s.settings.whatsapp
                                    ? '+' + s.settings.whatsapp
                                    : 'Not configured'}
                                </strong>
                              </div>
                              <div className="setting-line">
                                <span>Notification email</span>
                                <strong>{s.settings.notificationEmail || 'Not configured'}</strong>
                              </div>
                              <div className="setting-line">
                                <span>Human handoff / CRM webhook</span>
                                <strong>{s.settings.webhookUrl || 'Not configured'}</strong>
                              </div>
                              <p className="info-note">
                                WhatsApp opens a prefilled conversation summary. Email uses your
                                SMTP provider. Without SMTP, messages are available in the
                                notification preview below.
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="setting-line">
                                <span>Assistant name</span>
                                <strong>{s.settings.agentName}</strong>
                              </div>
                              <div className="setting-line">
                                <span>Uncertain answer</span>
                                <strong>
                                  {s.settings.handoffOnUncertain
                                    ? 'Offer human handoff'
                                    : 'Safe fallback'}
                                </strong>
                              </div>
                              <div className="setting-line">
                                <span>Pricing approval</span>
                                <strong>
                                  {s.settings.requireHumanForPricing
                                    ? 'Human confirmation required'
                                    : 'Approved knowledge only'}
                                </strong>
                              </div>
                              <div className="setting-line">
                                <span>Conversation retention</span>
                                <strong>{s.settings.retentionDays} days</strong>
                              </div>
                              <h3>Qualification questions</h3>
                              <p>Conversation: {s.settings.conversation?.tone||'friendly'} · General questions {s.settings.conversation?.allowGeneralQuestions===false?'disabled':'enabled'}. Use Edit settings to change conversation guidance without uploading files.</p>
                              {s.settings.qualificationQuestions.map((q: string, i: number) => (
                                <p key={i}>
                                  {i + 1}. {q}
                                </p>
                              ))}
                              <div className="info-note">
                                Use internal knowledge records for team-only notes. Apply the
                                agent’s behavior through these explicit settings.
                              </div>
                            </>
                          )}
                        </section>
                      ))}
                  </div>
                  {page === 'WhatsApp / Email' && (
                    <section className="panel form-panel">
                      <h2>Notification & integration outbox</h2>
                      <p>
                        Delivery retries are tracked. Demo notifications can be reviewed here
                        without sending email.
                      </p>
                      {data.events.map((event: any) => {
                        const payload = JSON.parse(event.payload);
                        return (
                          <details className="event" key={event.id}>
                            <summary>
                              <span>{payload.eventType || event.type}</span>
                              <Badge type={event.status === 'delivered' ? 'green' : 'amber'}>
                                {event.status}
                              </Badge>
                              <small>{age(event.created_at)}</small>
                            </summary>
                            <pre>{JSON.stringify(payload.context, null, 2)}</pre>
                            {['failed', 'preview'].includes(event.status) && (
                              <button
                                className="secondary"
                                onClick={() =>
                                  void action(
                                    () => send('admin/events/' + event.id + '/retry', {}),
                                    'Notification queued again',
                                  )
                                }
                              >
                                Retry delivery
                              </button>
                            )}
                          </details>
                        );
                      })}
                      {!data.events.length && (
                        <div className="empty">
                          No notifications yet. Submit contact details in your website widget.
                        </div>
                      )}
                    </section>
                  )}
                </>
              )}
            </>
          )}
          <footer className="workspace-footer">
            <span>Atlas · Built around your business</span>
            <span>
              <ShieldCheck size={14} /> Knowledge boundaries always on
            </span>
            <button className="text-button" onClick={refresh}>
              Refresh data
            </button>
          </footer>
        </main>
      </div>
      {editor && (
        <Modal title={editor.id ? 'Edit knowledge' : 'Add knowledge'} close={() => setEditor(null)}>
          <form onSubmit={saveKnowledge} className="form-panel">
            <div className="two-col compact">
              <label>
                Website
                <select
                  value={editor.site_id || ''}
                  onChange={(e) => setEditor({ ...editor, site_id: e.target.value || null })}
                >
                  <option value="">Shared across my websites</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Knowledge type
                <select
                  value={editor.kind}
                  onChange={(e) =>
                    setEditor({
                      ...editor,
                      kind: e.target.value,
                      visibility: e.target.value === 'instruction' ? 'internal' : editor.visibility,
                    })
                  }
                >
                  {[
                    'note',
                    'service',
                    'faq',
                    'instruction',
                    'offer',
                    'product',
                    'property',
                    'project',
                    'file',
                    'correction',
                  ].map((k) => (
                    <option key={k}>{k}</option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              Title / topic
              <input
                value={editor.title}
                required
                maxLength={200}
                onChange={(e) => setEditor({ ...editor, title: e.target.value })}
              />
            </label>
            <div className="two-col compact">
              <label>
                Visibility
                <select
                  value={editor.visibility}
                  onChange={(e) =>
                    setEditor({
                      ...editor,
                      visibility: e.target.value,
                      safe_answer: e.target.value === 'internal' ? '' : editor.safe_answer,
                    })
                  }
                  disabled={editor.kind === 'instruction'}
                >
                  <option value="public">Public — share with visitors</option>
                  <option value="private">Private — approved answer only</option>
                  <option value="internal">Internal — team guidance only</option>
                </select>
              </label>
              <label>
                Priority (0–100)
                <input
                  type="number"
                  min={0}
                  max={100}
                  required
                  value={editor.priority}
                  onChange={(e) => setEditor({ ...editor, priority: Number(e.target.value) })}
                />
              </label>
            </div>
            <label>
              {editor.visibility === 'public'
                ? 'Visitor-facing content'
                : 'Restricted source / internal notes'}
              <textarea
                required
                rows={5}
                value={editor.content}
                onChange={(e) => setEditor({ ...editor, content: e.target.value })}
              />
            </label>
            {editor.visibility === 'private' && (
              <label>
                Approved public answer
                <textarea
                  rows={4}
                  value={editor.safe_answer}
                  onChange={(e) => setEditor({ ...editor, safe_answer: e.target.value })}
                />
                <small>
                  Only this answer and its approved translations may reach the visitor. Leave blank
                  to keep the record unavailable for replies.
                </small>
              </label>
            )}
            {editor.visibility === 'internal' && (
              <div className="info-note">
                Free text stays team-only. To apply a rule, use JSON with requireHumanForPricing or
                handoffOnUncertain set to true or false. Higher priority rules override Agent
                Settings without passing the raw text to the reply generator.
              </div>
            )}
            <label>
              Expiry {editor.kind === 'offer' ? '(required)' : '(optional)'}
              <input
                type="datetime-local"
                required={editor.kind === 'offer'}
                value={
                  editor.expires_at
                    ? new Date(
                        new Date(editor.expires_at).getTime() -
                          new Date(editor.expires_at).getTimezoneOffset() * 60000,
                      )
                        .toISOString()
                        .slice(0, 16)
                    : ''
                }
                onChange={(e) =>
                  setEditor({
                    ...editor,
                    expires_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                  })
                }
              />
            </label>
            {editor.visibility !== 'internal' && (
              <details>
                <summary>Approved translations for the local provider</summary>
                {['ur', 'roman', 'ar', 'fr', 'zh', 'es', 'de'].map((lang) => (
                  <label key={lang}>
                    {lang}
                    <textarea
                      value={editor.translations[lang] || ''}
                      onChange={(e) =>
                        setEditor({
                          ...editor,
                          translations: { ...editor.translations, [lang]: e.target.value },
                        })
                      }
                    />
                  </label>
                ))}
              </details>
            )}
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editor.enabled}
                onChange={(e) => setEditor({ ...editor, enabled: e.target.checked })}
              />
              Enable this record
            </label>
            <button className="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save knowledge'}
            </button>
          </form>
        </Modal>
      )}
      {scanSite && <Modal title="Auto Website Analysis" close={()=>{setScanSite(null);void refresh();}}><ScanPanel site={scanSite}/></Modal>}
      {siteEditor && (
        <Modal
          title={
            sites.some((s) => s.id === siteEditor.id) ? 'Website settings' : 'Connect a website'
          }
          close={() => setSiteEditor(null)}
        >
          <form className="form-panel" onSubmit={saveSite}>
            <label className="checkbox-label"><input type="checkbox" checked={scanAfterSave} onChange={e=>setScanAfterSave(e.target.checked)}/>Open Auto Website Analysis after saving</label>
            <label>
              Website name
              <input
                required
                value={siteEditor.name}
                maxLength={100}
                onChange={(e) => setSiteEditor({ ...siteEditor, name: e.target.value })}
              />
            </label>
            <label>
              Site ID
              <input
                required
                pattern="[a-z0-9-]{3,64}"
                disabled={sites.some((s) => s.id === siteEditor.id)}
                value={siteEditor.id}
                onChange={(e) => setSiteEditor({ ...siteEditor, id: e.target.value.toLowerCase() })}
                placeholder="my-website"
              />
            </label>
            <label>
              Website origin
              <input
                type="url"
                required
                value={siteEditor.domain}
                onChange={(e) => setSiteEditor({ ...siteEditor, domain: e.target.value })}
              />
              <small>
                Use the exact origin, including https://. The widget can be embedded only on this
                website and the platform itself.
              </small>
            </label>
            <label>
              Assistant name
              <input
                required
                value={siteEditor.settings.agentName}
                onChange={(e) =>
                  setSiteEditor({
                    ...siteEditor,
                    settings: { ...siteEditor.settings, agentName: e.target.value },
                  })
                }
              />
            </label>
            <label>
              Welcome message
              <input
                required
                value={siteEditor.settings.welcome}
                onChange={(e) =>
                  setSiteEditor({
                    ...siteEditor,
                    settings: { ...siteEditor.settings, welcome: e.target.value },
                  })
                }
              />
            </label>
            <label>Conversation tone<select value={siteEditor.settings.conversation?.tone||'friendly'} onChange={e=>setSiteEditor({...siteEditor,settings:{...siteEditor.settings,conversation:{...{tone:'friendly',allowGeneralQuestions:true,customInstructions:''},...siteEditor.settings.conversation,tone:e.target.value}}})}><option value="friendly">Friendly and natural</option><option value="professional">Professional and concise</option></select></label>
            <fieldset className="chat-preferences"><legend>Welcome popup &amp; visitor experience</legend>
             <label className="checkbox-label"><input type="checkbox" checked={siteEditor.settings.chat?.autoOpen||false} onChange={e=>setSiteEditor({...siteEditor,settings:{...siteEditor.settings,chat:{...defaultChatSettings,...siteEditor.settings.chat,autoOpen:e.target.checked}}})}/> Open chat automatically when a visitor arrives</label>
             <label>Popup delay (seconds)<input type="number" min={0} max={120} value={siteEditor.settings.chat?.autoOpenDelaySeconds??5} onChange={e=>setSiteEditor({...siteEditor,settings:{...siteEditor.settings,chat:{...defaultChatSettings,...siteEditor.settings.chat,autoOpenDelaySeconds:Number(e.target.value)}}})}/></label>
             <label>Popup frequency<select value={siteEditor.settings.chat?.autoOpenFrequency||'session'} onChange={e=>setSiteEditor({...siteEditor,settings:{...siteEditor.settings,chat:{...defaultChatSettings,...siteEditor.settings.chat,autoOpenFrequency:e.target.value}}})}><option value="session">Once per browser tab visit</option><option value="page">On every page load</option></select></label>
             <label className="checkbox-label"><input type="checkbox" checked={siteEditor.settings.chat?.askVisitorName||false} onChange={e=>setSiteEditor({...siteEditor,settings:{...siteEditor.settings,chat:{...defaultChatSettings,...siteEditor.settings.chat,askVisitorName:e.target.checked}}})}/> Ask the visitor's name (optional, with Skip)</label>
             <label className="checkbox-label"><input type="checkbox" checked={siteEditor.settings.chat?.messageSound!==false} onChange={e=>setSiteEditor({...siteEditor,settings:{...siteEditor.settings,chat:{...defaultChatSettings,...siteEditor.settings.chat,messageSound:e.target.checked}}})}/> Offer reply sound notifications</label>
             <small>The visitor enables sound using the bell. Names come only from visitor input or optional browser autofill. Add {'{name}'} to the welcome message to personalize it. Minimize keeps new-message badges; Close suppresses them until chat is reopened.</small>
            </fieldset>
            <label className="checkbox-label"><input type="checkbox" checked={siteEditor.settings.conversation?.allowGeneralQuestions!==false} onChange={e=>setSiteEditor({...siteEditor,settings:{...siteEditor.settings,conversation:{...{tone:'friendly',allowGeneralQuestions:true,customInstructions:''},...siteEditor.settings.conversation,allowGeneralQuestions:e.target.checked}}})}/> Answer general questions as well as website enquiries</label>
            <label>Conversation guidance<textarea rows={4} maxLength={2000} value={siteEditor.settings.conversation?.customInstructions||''} placeholder="For example: keep answers short, ask one useful question, and respect a visitor who is only browsing." onChange={e=>setSiteEditor({...siteEditor,settings:{...siteEditor.settings,conversation:{...{tone:'friendly',allowGeneralQuestions:true,customInstructions:''},...siteEditor.settings.conversation,customInstructions:e.target.value}}})}/><small>Style and workflow guidance only. Add business facts in Knowledge Center. Do not put passwords or API keys here. Saved changes apply to the next reply without uploading files.</small></label>
            <div className="two-col compact">
              <label>
                Widget color
                <input
                  type="color"
                  value={siteEditor.settings.color}
                  onChange={(e) =>
                    setSiteEditor({
                      ...siteEditor,
                      settings: { ...siteEditor.settings, color: e.target.value },
                    })
                  }
                />
              </label>
              <label>
                Retention (days)
                <input
                  type="number"
                  min={7}
                  max={730}
                  value={siteEditor.settings.retentionDays}
                  onChange={(e) =>
                    setSiteEditor({
                      ...siteEditor,
                      settings: { ...siteEditor.settings, retentionDays: Number(e.target.value) },
                    })
                  }
                />
              </label>
            </div>
            <label>
              WhatsApp number
              <input
                value={siteEditor.settings.whatsapp}
                pattern="[0-9]{7,15}|^$"
                placeholder="923001234567"
                onChange={(e) =>
                  setSiteEditor({
                    ...siteEditor,
                    settings: { ...siteEditor.settings, whatsapp: e.target.value },
                  })
                }
              />
              <small>Country code + number; digits only.</small>
            </label>
            <label>
              Lead notification email
              <input
                type="email"
                value={siteEditor.settings.notificationEmail}
                onChange={(e) =>
                  setSiteEditor({
                    ...siteEditor,
                    settings: { ...siteEditor.settings, notificationEmail: e.target.value },
                  })
                }
              />
              <small>Approved visitor contact details are sent here when server SMTP is configured. Check delivery status in WhatsApp / Email settings.</small>
            </label>
            <label>
              Human handoff / integration webhook
              <input
                type="url"
                placeholder="https://crm.example.com/hooks/atlas"
                value={siteEditor.settings.webhookUrl}
                onChange={(e) =>
                  setSiteEditor({
                    ...siteEditor,
                    settings: { ...siteEditor.settings, webhookUrl: e.target.value },
                  })
                }
              />
              <small>Use an approved CRM or WhatsApp API integration to forward consented lead details. The WhatsApp number alone creates visitor handoff links; it does not send automatic WhatsApp alerts.</small>
            </label>
            <label>
              Qualification questions (one per line)
              <textarea
                value={siteEditor.settings.qualificationQuestions.join('\n')}
                onChange={(e) =>
                  setSiteEditor({
                    ...siteEditor,
                    settings: {
                      ...siteEditor.settings,
                      qualificationQuestions: e.target.value.split('\n').filter(Boolean),
                    },
                  })
                }
              />
            </label>
            {[
              ['enabled', 'Enable assistant'],
              ['handoffOnUncertain', 'Offer human handoff when uncertain'],
              ['requireHumanForPricing', 'Confirm live quotes and availability (website-listed prices can be shown)'],
            ].map(([key, label]) => (
              <label className="checkbox-label" key={key}>
                <input
                  type="checkbox"
                  checked={siteEditor.settings[key]}
                  onChange={(e) =>
                    setSiteEditor({
                      ...siteEditor,
                      settings: { ...siteEditor.settings, [key]: e.target.checked },
                    })
                  }
                />
                {label}
              </label>
            ))}
            <button className="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save website'}
            </button>
          </form>
        </Modal>
      )}
      {view && (
        <Modal title="Conversation details" close={() => setView(undefined)}>
          <div className="form-panel">
            <div className="conversation-meta">
              <Badge type="green">{view.conversation?.language}</Badge>
              <Badge type="amber">{view.conversation?.qualification}</Badge>
              <Badge>{view.conversation?.status}</Badge>
            </div>
            <h3>Visitor context</h3>
            <p>{view.conversation?.summary}</p>
            <div className="transcript">
              {view.messages.map((m: any) => (
                <div className={'transcript-message ' + m.role} key={m.id}>
                  <small>
                    {m.role === 'user' ? 'Visitor' : 'Assistant'} ·{' '}
                    {new Date(m.created_at).toLocaleTimeString()}
                  </small>
                  <p>{m.content}</p>
                  {m.role === 'assistant' && (
                    <small>
                      {JSON.parse(m.metadata || '{}').provider || 'assistant'}
                      {typeof JSON.parse(m.metadata || '{}').elapsedMs==='number'?' · '+(JSON.parse(m.metadata || '{}').elapsedMs/1000).toFixed(2)+'s':''}
                      {JSON.parse(m.metadata || '{}').uncertain ? ' · Needed confirmation' : ''}
                      {JSON.parse(m.metadata || '{}').fallbackReason ? ' · '+JSON.parse(m.metadata || '{}').fallbackReason : ''}
                      {JSON.parse(m.metadata || '{}').sourceIds?.length
                        ? ' · Sources: ' + JSON.parse(m.metadata || '{}').sourceIds.join(', ')
                        : ''}
                    </small>
                  )}
                  {m.role === 'assistant' && (
                    <button
                      className="text-button"
                      onClick={() => {
                        setTeach({
                          title: '',
                          content: '',
                          site_id: view.conversation.site_id,
                          original: m.content,
                        });
                        setView(undefined);
                        setPage('Teach AI');
                      }}
                    >
                      Correct this answer <Sparkles size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="card-actions">
              <button
                className="secondary"
                onClick={async () => {
                  await action(() =>
                    send(
                      'admin/conversations/' + view.conversation.id,
                      { status: view.conversation.status === 'closed' ? 'active' : 'closed' },
                      'PATCH',
                    ),
                  );
                  setView(undefined);
                }}
              >
                {view.conversation?.status === 'closed' ? 'Reopen' : 'Close'} conversation
              </button>
              <button
                className="danger-button"
                onClick={async () => {
                  if (
                    confirm(
                      'Permanently delete this conversation, contact details, and related notifications?',
                    )
                  ) {
                    await action(
                      () =>
                        api('admin/conversations/' + view.conversation.id, { method: 'DELETE' }),
                      'Conversation deleted',
                    );
                    setView(undefined);
                  }
                }}
              >
                <Trash2 size={15} />
                Delete data
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
function ConversationTable({ rows, open }: { rows: any[]; open: (id: string) => void }) {
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Visitor / enquiry</th>
            <th>Website</th>
            <th>Language</th>
            <th>Intent</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id}>
              <td>
                <button className="row-link" onClick={() => open(c.id)}>
                  <span className="avatar visitor">{c.language.toUpperCase().slice(0, 2)}</span>
                  <span>
                    <strong>
                      {c.summary === 'New conversation' ? 'New visitor' : c.summary.slice(0, 60)}
                    </strong>
                    <small>
                      {age(c.created_at)} · {c.id.slice(0, 8)}
                    </small>
                  </span>
                </button>
              </td>
              <td>{c.site_name}</td>
              <td>{c.language.toUpperCase()}</td>
              <td>
                <Badge
                  type={
                    c.qualification === 'hot'
                      ? 'amber'
                      : c.qualification === 'warm'
                        ? 'green'
                        : 'gray'
                  }
                >
                  {c.qualification}
                </Badge>
              </td>
              <td>
                <Badge type={c.status === 'human' ? 'amber' : 'gray'}>{c.status}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}




